import { useEffect, useRef } from 'react';

/**
 * Scoped, input-aware keyboard shortcuts.
 *
 * The legacy dashboard attached an arrow-key handler to `window` with the annotation
 * state in its dependency array, so it detached and reattached on every keystroke and
 * happily ate typing inside text inputs. A separate capture-phase script
 * (`annotation-input-guard.js`) existed purely to undo that. This hook removes the need
 * for it:
 *
 *  1. bindings live in a ref, so the listener is registered ONCE per mount;
 *  2. the "is the user typing?" bail-out is inside the only handler that exists;
 *  3. it attaches to a scope element, so other routes and modals never see these keys;
 *  4. `isComposing` is respected, so IME input is not hijacked;
 *  5. unbound keys are never preventDefault-ed, so browser shortcuts still work.
 */

const EDITABLE_TAGS = new Set(['input', 'textarea', 'select']);

export function isEditableTarget(element) {
  if (!element || !(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;
  return EDITABLE_TAGS.has(element.tagName.toLowerCase());
}

export function comboOf(event) {
  const parts = [];
  if (event.metaKey || event.ctrlKey) parts.push('mod');
  if (event.altKey) parts.push('alt');
  if (event.shiftKey) parts.push('shift');
  parts.push(event.key.length === 1 ? event.key.toLowerCase() : event.key);
  return parts.join('+');
}

/**
 * @param {Object} bindings  e.g. { h: fn, 'mod+s': { run, allowInInput: true } }
 * @param {Object} options   { scopeRef, enabled }
 */
export function useHotkeys(bindings, { scopeRef, enabled = true } = {}) {
  const latest = useRef(bindings);
  latest.current = bindings;

  useEffect(() => {
    if (!enabled) return undefined;
    const node = scopeRef?.current ?? document;

    const onKeyDown = (event) => {
      if (event.isComposing || event.keyCode === 229) return;
      if (event.defaultPrevented) return;

      const entry = latest.current[comboOf(event)] ?? latest.current[event.key];
      if (!entry) return;

      const { run, allowInInput = false } =
        typeof entry === 'function' ? { run: entry } : entry;

      if (!allowInInput && isEditableTarget(event.target)) return;

      event.preventDefault();
      run(event);
    };

    node.addEventListener('keydown', onKeyDown);
    return () => node.removeEventListener('keydown', onKeyDown);
    // `bindings` is deliberately excluded: the ref above keeps handlers fresh without
    // re-subscribing, which is the whole point.
  }, [scopeRef, enabled]);
}
